import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'fininteract-sidebar-collapsed';

interface SidebarContextValue {
  // Desktop: whether the sidebar is collapsed to an icon-only rail. Persisted so it survives
  // reloads/sessions -- a one-time preference, not something reset every visit.
  collapsed: boolean;
  toggleCollapsed: () => void;
  // Mobile: whether the slide-out drawer (opened via the hamburger in AppMobileTopBar) is open.
  mobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Private browsing / storage disabled -- collapse preference just won't persist.
    }
  }, [collapsed]);

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        toggleCollapsed: () => setCollapsed((c) => !c),
        mobileOpen,
        openMobile: () => setMobileOpen(true),
        closeMobile: () => setMobileOpen(false),
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextValue => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within a SidebarProvider');
  return ctx;
};

// Every /app page that renders AppSidebar applies this to its outer wrapper so content
// clears the fixed sidebar at whatever width the user's collapse preference calls for.
export const useSidebarContentClass = (): string => {
  const { collapsed } = useSidebar();
  return collapsed ? 'md:pl-16' : 'md:pl-60';
};
